import React, { useState, useEffect, useCallback } from "react";
import { Table, Button, Form, ButtonGroup, Alert, Badge } from "react-bootstrap";
import { FaEdit, FaTrash, FaPlug } from "react-icons/fa";
import UserService from "../services/user.service";

// Leeres Formular fuer eine API-Konfiguration. provider/auth_type sind als
// sinnvolle Vorbelegung gesetzt und ueber die Auswahl erweiterbar.
const emptyForm = {
  api_name: "",
  provider: "voicetreff",
  base_url: "",
  auth_type: "APIKEY_HMAC",
  api_key: "",
  api_secret: "",
  enabled: true,
  description: "",
};

// Gemeinsame Verwaltungs-Oberflaeche fuer die API-Registry (Anlegen, Bearbeiten,
// Loeschen, Verbindungstest). Wird sowohl von der eigenstaendigen Seite
// (BoardApiConfig) als auch vom Modal im Produkt-Tab (ApiRegistryManagerModal)
// verwendet, damit es keine Doppellogik gibt.
//
// Props:
//   active   – steuert, wann (neu) geladen wird (z. B. Modal-Sichtbarkeit oder
//              dauerhaft true auf einer eigenen Seite). Default: true.
//   onChanged – optionaler Callback nach create/update/delete (z. B. zum
//              Neuladen einer Produkt-Zuordnungsliste).
const ApiRegistryManagerPanel = ({ active = true, onChanged }) => {
  const [list, setList] = useState([]);
  const [form, setForm] = useState({ ...emptyForm });
  const [editingId, setEditingId] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [info, setInfo] = useState("");
  const [testResults, setTestResults] = useState({});

  const extract = (err) =>
    (err.response && err.response.data && err.response.data.message) ||
    err.message ||
    String(err);

  const load = useCallback(() => {
    setError("");
    UserService.getApiRegistry().then(
      (res) => setList(Array.isArray(res.data) ? res.data : []),
      (err) => setError(extract(err))
    );
  }, []);

  const resetForm = useCallback(() => {
    setForm({ ...emptyForm });
    setEditingId(null);
    setInfo("");
  }, []);

  useEffect(() => {
    if (active) {
      load();
      resetForm();
    }
  }, [active, load, resetForm]);

  const handleChange = (e) => {
    const { name, type, checked, value } = e.target;
    setForm((f) => ({ ...f, [name]: type === "checkbox" ? checked : value }));
  };

  const startEdit = (api) => {
    setEditingId(api.api_id);
    setForm({
      api_name: api.api_name || "",
      provider: api.provider || "",
      base_url: api.base_url || "",
      auth_type: api.auth_type || "APIKEY_HMAC",
      api_key: api.api_key || "",
      api_secret: "",
      enabled: !!api.enabled,
      description: api.description || "",
    });
    setError("");
    setInfo(
      api.has_secret
        ? "Secret ist hinterlegt. Feld leer lassen, um es unveraendert zu uebernehmen."
        : "Fuer den Verbindungstest ist ein Secret erforderlich."
    );
  };

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setInfo("");
    if (!form.api_name.trim()) {
      setError("API-Name ist erforderlich.");
      return;
    }
    if (!form.base_url.trim()) {
      setError("Basis-URL ist erforderlich.");
      return;
    }
    try {
      setLoading(true);
      if (editingId) {
        await UserService.updateApiRegistry(editingId, form);
      } else {
        await UserService.createApiRegistry(form);
      }
      resetForm();
      load();
      if (typeof onChanged === "function") onChanged();
    } catch (err) {
      setError(extract(err));
    } finally {
      setLoading(false);
    }
  };

  const remove = async (api) => {
    // eslint-disable-next-line no-alert
    if (!window.confirm(`API "${api.api_name}" wirklich loeschen?`)) return;
    setError("");
    try {
      setLoading(true);
      await UserService.deleteApiRegistry(api.api_id);
      if (editingId === api.api_id) resetForm();
      load();
      if (typeof onChanged === "function") onChanged();
    } catch (err) {
      setError(extract(err));
    } finally {
      setLoading(false);
    }
  };

  const test = async (api) => {
    setTestResults((r) => ({ ...r, [api.api_id]: { loading: true } }));
    try {
      const res = await UserService.testApiRegistry(api.api_id);
      const d = res.data || {};
      setTestResults((r) => ({
        ...r,
        [api.api_id]: {
          ok: !!d.ok,
          text: d.ok
            ? `Verbindung OK (${d.latency_ms} ms)`
            : `Fehlgeschlagen (HTTP ${d.http_status != null ? d.http_status : "-"})`,
        },
      }));
    } catch (err) {
      const d = (err.response && err.response.data) || {};
      setTestResults((r) => ({
        ...r,
        [api.api_id]: { ok: false, text: d.message || extract(err) },
      }));
    }
  };

  return (
    <>
      {error && <Alert variant="danger">{error}</Alert>}
      {info && <Alert variant="info">{info}</Alert>}

      <h6>Vorhandene APIs</h6>
      <Table responsive striped bordered hover size="sm" className="mb-4">
        <thead>
          <tr>
            <th>ID</th>
            <th>Name</th>
            <th>Anbieter</th>
            <th>Basis-URL</th>
            <th>Auth</th>
            <th>Secret</th>
            <th>Aktiv</th>
            <th>Aktionen</th>
          </tr>
        </thead>
        <tbody>
          {list.length === 0 && (
            <tr>
              <td colSpan={8} className="text-muted text-center">
                Noch keine API angelegt.
              </td>
            </tr>
          )}
          {list.map((api) => {
            const tr = testResults[api.api_id];
            return (
              <tr key={api.api_id}>
                <td>{api.api_id}</td>
                <td>{api.api_name}</td>
                <td>{api.provider || <span className="text-muted">–</span>}</td>
                <td style={{ maxWidth: 220, wordBreak: "break-all" }}>{api.base_url}</td>
                <td>{api.auth_type}</td>
                <td>
                  {api.has_secret ? (
                    <Badge bg="success">ja</Badge>
                  ) : (
                    <Badge bg="secondary">nein</Badge>
                  )}
                </td>
                <td>
                  {api.enabled ? (
                    <Badge bg="success">aktiv</Badge>
                  ) : (
                    <Badge bg="secondary">inaktiv</Badge>
                  )}
                </td>
                <td>
                  <ButtonGroup>
                    <Button
                      size="sm"
                      variant="warning"
                      title="Bearbeiten"
                      onClick={() => startEdit(api)}
                    >
                      <FaEdit />
                    </Button>
                    <Button
                      size="sm"
                      variant="info"
                      title="Verbindung testen"
                      disabled={tr && tr.loading}
                      onClick={() => test(api)}
                    >
                      <FaPlug />
                    </Button>
                    <Button
                      size="sm"
                      variant="danger"
                      title="Loeschen"
                      onClick={() => remove(api)}
                    >
                      <FaTrash />
                    </Button>
                  </ButtonGroup>
                  {tr && !tr.loading && (
                    <div className={tr.ok ? "text-success mt-1" : "text-danger mt-1"} style={{ fontSize: "0.8rem" }}>
                      {tr.text}
                    </div>
                  )}
                  {tr && tr.loading && (
                    <div className="text-muted mt-1" style={{ fontSize: "0.8rem" }}>
                      Teste…
                    </div>
                  )}
                </td>
              </tr>
            );
          })}
        </tbody>
      </Table>

      <h6>{editingId ? "API bearbeiten" : "Neue API anlegen"}</h6>
      <Form onSubmit={submit}>
        <Form.Group className="mt-2">
          <Form.Label>Name</Form.Label>
          <Form.Control
            type="text"
            name="api_name"
            placeholder="z. B. Voicetreff Partner-API"
            value={form.api_name}
            onChange={handleChange}
          />
        </Form.Group>

        <Form.Group className="mt-2">
          <Form.Label>Anbieter</Form.Label>
          <Form.Control
            type="text"
            name="provider"
            placeholder="z. B. voicetreff"
            value={form.provider}
            onChange={handleChange}
          />
        </Form.Group>

        <Form.Group className="mt-2">
          <Form.Label>Basis-URL</Form.Label>
          <Form.Control
            type="text"
            name="base_url"
            placeholder="https://www.voicetreff.com"
            value={form.base_url}
            onChange={handleChange}
          />
        </Form.Group>

        <Form.Group className="mt-2">
          <Form.Label>Auth-Verfahren</Form.Label>
          <Form.Select name="auth_type" value={form.auth_type} onChange={handleChange}>
            <option value="APIKEY_HMAC">API-Key + HMAC-Signatur</option>
            <option value="SHARED_SECRET">Shared Secret</option>
          </Form.Select>
        </Form.Group>

        <Form.Group className="mt-2">
          <Form.Label>API-Key</Form.Label>
          <Form.Control
            type="text"
            name="api_key"
            placeholder="Oeffentliche Schluesselkennung"
            value={form.api_key}
            onChange={handleChange}
          />
        </Form.Group>

        <Form.Group className="mt-2">
          <Form.Label>API-Secret</Form.Label>
          <Form.Control
            type="password"
            name="api_secret"
            placeholder={editingId ? "Leer lassen = unveraendert" : "Geheimes Secret"}
            value={form.api_secret}
            onChange={handleChange}
            autoComplete="new-password"
          />
          <Form.Text className="text-muted">
            Wird verschluesselt gespeichert und nie im Klartext angezeigt.
          </Form.Text>
        </Form.Group>

        <Form.Group className="mt-2">
          <Form.Label>Beschreibung</Form.Label>
          <Form.Control
            as="textarea"
            rows={2}
            name="description"
            value={form.description}
            onChange={handleChange}
          />
        </Form.Group>

        <Form.Check
          type="checkbox"
          className="mt-2"
          id="apiEnabled"
          name="enabled"
          label="Aktiv"
          checked={form.enabled}
          onChange={handleChange}
        />

        <div className="d-flex gap-2 mt-3">
          <Button variant="primary" type="submit" disabled={loading}>
            {loading ? "Wird gespeichert…" : editingId ? "Aenderungen speichern" : "API anlegen"}
          </Button>
          {editingId && (
            <Button variant="outline-secondary" type="button" onClick={resetForm} disabled={loading}>
              Abbrechen
            </Button>
          )}
        </div>
      </Form>
    </>
  );
};

export default ApiRegistryManagerPanel;
